import { type LoaderFunctionArgs, type MetaFunction, json } from '@remix-run/node';
import { Link, useLoaderData } from '@remix-run/react';
import { GeneralErrorBoundary } from '~/components/error-boundary.tsx';
import { buildMeta } from '~/utils/meta.js';
import { type SignupType, parseSignupType } from '~/utils/signup-type.server.ts';
import { zJson } from '~/utils/zod.ts';
import { SignupForm, guestOnboardingSchema } from '../resources+/_auth+/signup.tsx';
import { authStyles } from './_layout.tsx';

const onboardingParamsSchema = zJson().pipe(guestOnboardingSchema);

export async function loader({ context, request }: LoaderFunctionArgs) {
  await context.authSession.requireAnonymous();

  const params = new URL(request.url).searchParams;

  const onboardingResult = onboardingParamsSchema.safeParse(params.get('onboarding') ?? null);

  return json({
    type: parseSignupType(params.get('type') ?? ''),
    onboarding: onboardingResult.success ? onboardingResult.data : undefined,
    redirectTo: params.get('redirectTo') ?? undefined,
    email: params.get('email') ?? undefined,
  });
}

export const meta: MetaFunction = (args) => {
  return buildMeta(args, { title: 'Signup | hostU' });
};

export default function SignupRoute() {
  const { redirectTo, type, onboarding, email } = useLoaderData<typeof loader>();
  return (
    <div className="container flex flex-col justify-center pt-24 pb-32">
      <div className="text-center">
        <h1 className={authStyles.headingClassName}>{getSignupHeaders(type)}</h1>
        <p className={authStyles.subHeadingClassName}>Use your .edu email to get verified now.</p>
      </div>
      <SignupForm
        redirectTo={redirectTo}
        type={type}
        email={email}
        onboarding={
          onboarding
            ? {
                type: 'guest',
                payload: {
                  address: onboarding.address,
                  from: new Date(onboarding.from),
                  to: new Date(onboarding.to),
                },
              }
            : undefined
        }
      />
      <div className="flex xs:flex-row flex-col items-center justify-center gap-2 pt-6">
        <span className="text-muted-foreground">Already have an account?</span>
        <Link to="/login">Sign in here</Link>
      </div>
    </div>
  );
}

function getSignupHeaders(type: SignupType) {
  switch (type) {
    case 'host-full':
      return 'List now';
    case 'guest-full':
      return 'Search for a stay';
    case 'host-partial':
    case 'guest-partial':
      return 'Welcome to hostU';
  }
}

export function ErrorBoundary() {
  return <GeneralErrorBoundary />;
}
